import { Slot } from 'radix-ui';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE EXEMPLAR. EVERY OTHER PRIMITIVE IN THIS DIRECTORY FOLLOWS THIS SHAPE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. **`forwardRef`, always.** Radix composes by cloning a child and handing it a ref —
 *    `<Tooltip.Trigger asChild><Button/></Tooltip.Trigger>` positions against a node it can
 *    only reach through the ref. A primitive that drops its ref works alone and fails the
 *    moment it is composed, which is the worst time to find out.
 *
 * 2. **Extend the native props.** `ButtonHTMLAttributes` means `type`, `form`, `aria-*`,
 *    `data-*` and every event handler work without being enumerated here. A hand-picked prop
 *    list is a list somebody has to extend every time a caller needs `formNoValidate`.
 *
 * 3. **`className` LAST in `cn()`.** `tailwind-merge` is last-wins, so this is what makes a
 *    caller's override actually override rather than depend on stylesheet order.
 *
 * 4. **No colour literal, ever.** Only semantic tokens (`bg-accent`, `text-fg-muted`). A
 *    `text-slate-500` here is correct in one theme by accident and wrong in the other by
 *    construction.
 *
 * 5. **A test file next to it, and it asserts BEHAVIOUR plus axe.** Rendering without
 *    throwing is not a test.
 *
 * ── AND THE ONE THING THAT IS SPECIFIC TO A BUTTON ─────────────────────────
 * **`disabled` is not used for the busy state, and that is an accessibility decision.** A
 * disabled button is removed from the tab order, so a keyboard user whose focus was on it
 * when the request started is dumped back to the top of the document, and a screen reader
 * announces nothing about why. `loading` therefore sets `aria-disabled` and `aria-busy` and
 * swallows the click, leaving the control focusable and its name readable. `disabled` stays
 * available for what it actually means: a control that is not applicable at all.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover active:bg-accent-active border-transparent',
  secondary:
    'bg-surface text-fg border-border-strong hover:bg-surface-sunken active:bg-surface-sunken',
  ghost: 'bg-transparent text-fg border-transparent hover:bg-surface-sunken active:bg-surface-sunken',
  danger: 'bg-danger text-danger-fg hover:bg-danger-hover active:bg-danger-hover border-transparent',
};

/**
 * `min-h` rather than a fixed height, and it is WCAG 2.5.8 (Target Size, Minimum, AA in 2.2):
 * a control must be at least 24×24 CSS px. `sm` is 32px and the others are larger, so all
 * three clear it with room — but a fixed `h-8` would also clip a button whose label wraps at
 * a user's larger text size, which 1.4.4 requires to keep working.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-8 px-3 py-1 text-sm gap-1.5',
  md: 'min-h-10 px-4 py-2 text-sm gap-2',
  lg: 'min-h-12 px-5 py-2.5 text-base gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Render the child element instead of a `<button>`, forwarding these props onto it. This is
   * how a link becomes a button-shaped control without nesting an `<a>` inside a `<button>`,
   * which is invalid HTML and produces two tab stops.
   */
  asChild?: boolean;
  /** See the header: this is `aria-busy`, NOT `disabled`. */
  loading?: boolean;
  /**
   * Announced while `loading`. Defaults to a generic string; give it something specific when
   * the wait is long enough to wonder about ("Placing call…").
   */
  loadingLabel?: string;
  /** Decorative only — it is marked `aria-hidden`, so never put meaning here. */
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    asChild = false,
    loading = false,
    loadingLabel = 'Working…',
    iconLeft,
    iconRight,
    className,
    children,
    disabled,
    onClick,
    type,
    ...rest
  },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      ref={ref}
      /**
       * `type="button"` unless told otherwise. HTML's default is `submit`, so a button placed
       * in a form for any other purpose submits it — which on this console would mean a
       * "Test call" button silently saving a half-edited extension.
       *
       * Not applied when `asChild`: the child may be an `<a>`, and `type` on an anchor is a
       * content-type hint, not a button behaviour.
       */
      {...(asChild ? {} : { type: type ?? 'button' })}
      disabled={disabled}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      onClick={(event) => {
        // `aria-disabled` is a promise to the user, not to the browser — the click still
        // fires and has to be stopped here.
        if (loading) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      className={cn(
        'inline-flex items-center justify-center rounded-md border font-medium',
        'transition-colors duration-150 select-none',
        // `disabled:` and `aria-disabled` are styled identically so the two states look the
        // same to a sighted user while remaining different to the accessibility tree.
        'disabled:pointer-events-none disabled:opacity-50',
        'aria-disabled:opacity-70 aria-disabled:cursor-progress',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {iconLeft ? (
        <span aria-hidden="true" className="shrink-0">
          {iconLeft}
        </span>
      ) : null}
      {/**
       * ── `Slottable` IS WHAT MAKES `asChild` WORK WITH SIBLINGS ─────────────
       * `Slot.Root` merges its props onto A SINGLE child element and throws
       * "Slot failed to slot onto its children" for anything else. This component always
       * renders an array — the icon slots and the live region are siblings of `children`
       * even when every one of them is `null`, because React still passes the array.
       *
       * `Slottable` marks WHICH member of that array is the real child, so Radix merges onto
       * the caller's `<a>` and re-parents the icons inside it rather than refusing. Found by
       * the `asChild` test, which failed outright without it.
       */}
      <Slot.Slottable>{children}</Slot.Slottable>
      {iconRight ? (
        <span aria-hidden="true" className="shrink-0">
          {iconRight}
        </span>
      ) : null}
      {/**
       * A live region rather than a spinner glyph. The visual affordance is the cursor and the
       * opacity change; this is the part a screen reader gets, and it is `polite` because a
       * button becoming busy is not worth interrupting whatever is being read.
       */}
      {loading ? (
        <span role="status" className="sr-only-focusable absolute">
          {loadingLabel}
        </span>
      ) : null}
    </Comp>
  );
});
